import {
  AddressModel,
  getFullAddress,
} from "../../../../service/models/address_model";
import { Complaintmodel } from "../../../../service/models/complaint_model";

export interface HomePlatformDevice {
  id: string;
  label?: string;
  orgName?: string;
  productName?: string;
  firmwareVersion?: string;
  hardwareVersion?: string;
  batchNumber?: string;
  mfgDate?: string;
  // false means the installation exists but WCR hasn't been filled on Home
  // App yet — a ticket can still be created for this device.
  wcrPresent?: boolean;
  raw?: any;
}

export interface HomePlatformComplaint {
  id: string;
  subject?: string;
  status?: string;
  createdAt?: string;
  deviceId?: string;
  raw?: any;
}

export interface HomePlatformLookupResult {
  isHomePlatformComplaint: boolean;
  productName?: string;
  customerName?: string;
  customerAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerPincode?: string;
  dealerId?: string;
  orgName?: string;
  devices: HomePlatformDevice[];
  complaints: HomePlatformComplaint[];
  raw?: any;
}

export const parseDeviceTokens = (deviceId?: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of (deviceId ?? "").split(",")) {
    const id = raw.trim().toLowerCase();
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};

export const sanitizePhoneNumber = (value: string) =>
  value.replace(/^\+91/, "").replace(/\D/g, "").slice(0, 10);

export const isValidPhoneNumber = (value?: string) =>
  !!value && /^[6-9]\d{9}$/.test(value);

export const normaliseHomePlatformLookup = (
  payload: any,
): HomePlatformLookupResult => {
  const records = Array.isArray(payload?.data)
    ? payload.data
    : [payload?.data ?? payload];

  const devices: HomePlatformDevice[] = records
    .map((item: any) => {
      const deviceId = item?.deviceId;

      if (!deviceId) return null;

      return {
        id: String(deviceId),
        label: String(deviceId),
        wcrPresent: !!item?.wcrPresent,
        raw: item,
      };
    })
    .filter(Boolean) as HomePlatformDevice[];

  const pickFromRecords = (keyFn: (item: any) => any) => {
    for (const item of records) {
      const value = keyFn(item);
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return undefined;
  };

  let returnableData = {
    isHomePlatformComplaint: records.length > 0,

    customerName: pickFromRecords((i) => i?.customerDetails?.name),
    customerAddress: pickFromRecords((i) => i?.customerDetails?.fullAddress),
    customerCity: pickFromRecords((i) => i?.customerDetails?.city),
    customerState: pickFromRecords((i) => i?.customerDetails?.state),
    customerPincode: pickFromRecords((i) => i?.customerDetails?.pincode),

    dealerId: pickFromRecords((i) => i?.dealership?.id),
    orgName: pickFromRecords((i) => i?.org || i?.dealership?.name),

    devices,
    complaints: [],
    raw: records,
  };
  return returnableData;
};

export const buildAddressFromLookup = (
  lookup?: HomePlatformLookupResult,
): AddressModel | undefined => {
  if (!lookup) return undefined;

  const fullAddress =
    lookup.customerAddress ||
    getFullAddress({
      line1: "",
      city: lookup.customerCity ?? "",
      state: lookup.customerState ?? "",
      country: "India",
      pincode: Number(lookup.customerPincode) || 0,
    });

  if (
    !fullAddress &&
    !lookup.customerCity &&
    !lookup.customerState &&
    !lookup.customerPincode
  ) {
    return undefined;
  }

  return {
    line1: lookup.customerAddress ?? "",
    city: lookup.customerCity ?? "",
    state: lookup.customerState ?? "",
    country: "India",
    pincode: Number(lookup.customerPincode) || 0,
    full_address: fullAddress,
  };
};

export type LockableField =
  | "org"
  | "complainant_name"
  | "complainant_mobile"
  | "address"
  | "state"
  | "pincode"
  | "dealer_id";

export const getLockedFieldsFromLookup = (
  lookup: HomePlatformLookupResult | undefined,
  selectedDeviceId?: string,
  record?: any,
): Set<LockableField> => {
  const locked = new Set<LockableField>();
  if (!lookup) return locked;

  const selectedDevice =
    lookup.devices.find((device) => device.id === selectedDeviceId) ??
    lookup.devices[0];
  const deviceRecord = record ?? selectedDevice?.raw;
  const address = buildAddressFromLookup(lookup);

  if (lookup.orgName || deviceRecord?.org) locked.add("org");

  const customerName =
    deviceRecord?.customerName ||
    deviceRecord?.customerDetails?.name ||
    lookup.customerName;
  if (customerName) locked.add("complainant_name");

  const customerMobile =
    deviceRecord?.customerPhone || deviceRecord?.customerDetails?.contact;
  if (customerMobile) locked.add("complainant_mobile");

  if (address?.full_address) locked.add("address");

  const state =
    deviceRecord?.state ||
    deviceRecord?.customerDetails?.state ||
    lookup.customerState;
  if (state) locked.add("state");

  const pincode =
    deviceRecord?.pincode ||
    deviceRecord?.customerDetails?.pincode ||
    lookup.customerPincode;
  if (pincode) locked.add("pincode");

  const dealerId = deviceRecord?.dealership?.id || lookup.dealerId;
  if (dealerId) locked.add("dealer_id");

  return locked;
};

export const mergeComplaintWithLookup = (
  complaint: Complaintmodel,
  lookup: HomePlatformLookupResult | undefined,
  selectedDeviceId?: string,
): Complaintmodel => {
  if (!lookup) return complaint;

  const selectedDevice =
    lookup.devices.find((device) => device.id === selectedDeviceId) ??
    lookup.devices[0];
  const address = buildAddressFromLookup(lookup);

  const selectedDeviceRecord = selectedDevice?.raw;
  const customerNameFromDevice =
    selectedDeviceRecord?.customerName ||
    selectedDeviceRecord?.customerDetails?.name;

  const deviceState = selectedDeviceRecord?.state;
  const deviceCity = selectedDeviceRecord?.city;
  const devicePincode = selectedDeviceRecord?.pincode;

  return {
    ...complaint,
    device_id: complaint.device_id || selectedDevice?.id,
    org: lookup.orgName || complaint.org,
    complainant_name:
      complaint.complainant_name ||
      customerNameFromDevice ||
      lookup.customerName ||
      "",
    reporter_name:
      complaint.reporter_name ||
      customerNameFromDevice ||
      lookup.customerName ||
      "",
    reporter_mobile:
      complaint.reporter_mobile || complaint.complainant_mobile || "",
    address: address ?? complaint.address,
    state: deviceState || lookup.customerState || complaint.state,
    city: deviceCity || lookup.customerCity || complaint.city,
    pincode: devicePincode || lookup.customerPincode || complaint.pincode,
    dealer_id: complaint.dealer_id || lookup.dealerId,
  };
};
